import { Injectable } from '@angular/core';
import { ApiCrudService } from '../../../../core/services/api/api-crud.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { PaginatedResponse } from '../../../../core/services/api/paginated-response';
import { StationMissingDataDailyModel } from '../../models/station-missing-data-daily';
import { StationMissingDataDailyAdapter } from '../../adapters/station-missing-data-daily';
import { environment } from '@env/environment';

const BASE_URL = environment.apiBaseUrl + '/weather-station/data/daily/';

@Injectable({
  providedIn: 'root',
})
export class MissingStationDataManagementApiService extends ApiCrudService<StationMissingDataDailyModel> {
  private readonly skipLoadingHeaders = new HttpHeaders().set(
    'X-Skip-Loading',
    'true',
  );

  constructor(http: HttpClient) {
    super(http, BASE_URL);
  }

  override getAll(): Observable<StationMissingDataDailyModel[]> {
    return this.http.get<any>(`${this.baseUrl}missing-data`).pipe(
      map((response: any) => {
        return response.data.map(StationMissingDataDailyAdapter.fromApi);
      }),
    );
  }

  /**
   * Busca dados faltantes com paginação server-side
   */
  getPaginated(
    page: number,
    size: number,
    sortField: string = 'stationName',
    sortDirection: string = 'asc',
  ): Observable<PaginatedResponse<StationMissingDataDailyModel>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', `${sortField},${sortDirection}`);

    return this.http
      .get<any>(`${this.baseUrl}missing-data/paginated`, { params })
      .pipe(
        map((response) => {
          if (response?.data) {
            const data = response.data;

            if (data.content && Array.isArray(data.content)) {
              const content = data.content.map(
                StationMissingDataDailyAdapter.fromApi,
              );

              return {
                content: content,
                totalElements: data.totalElements || 0,
                totalPages: data.totalPages || 0,
                size: content.length || size,
                number: data.currentPage ?? data.number ?? page,
                hasNext: data.hasNext || false,
                hasPrevious: data.hasPrevious || false,
              } as PaginatedResponse<StationMissingDataDailyModel>;
            }

            if (Array.isArray(data)) {
              const allData = data.map(StationMissingDataDailyAdapter.fromApi);
              const startIndex = page * size;
              const endIndex = startIndex + size;
              const pageContent = allData.slice(startIndex, endIndex);

              return {
                content: pageContent,
                totalElements: allData.length,
                totalPages: Math.ceil(allData.length / size),
                size: size,
                number: page,
                hasNext: endIndex < allData.length,
                hasPrevious: page > 0,
              } as PaginatedResponse<StationMissingDataDailyModel>;
            }
          }

          return {
            content: [],
            totalElements: 0,
            totalPages: 0,
            size: size,
            number: page,
            hasNext: false,
            hasPrevious: false,
          } as PaginatedResponse<StationMissingDataDailyModel>;
        }),
      );
  }

  override getById(
    id: number | string,
  ): Observable<StationMissingDataDailyModel> {
    return this.http
      .get<any>(`${this.baseUrl}missing-data/${id}`)
      .pipe(
        map((response: any) =>
          StationMissingDataDailyAdapter.fromApi(response.data),
        ),
      );
  }

  /**
   * Retorna o total de dados faltantes nos últimos 30 dias
   */
  getMissingDataCount(): Observable<number> {
    // Adiciona header para desabilitar loading global
    const headers = { 'X-Skip-Loading': 'true' };

    return this.http
      .get<any>(`${this.baseUrl}missing-data/count`, { headers })
      .pipe(
        map((response: any) => {
          // Extrai o número da mensagem usando regex
          const message = response.message || '';
          const match = message.match(/\d+$/); // Pega os números no final da string
          return match ? Number.parseInt(match[0], 10) : 0;
        }),
      );
  }

  /**
   * Busca dados faltantes por data específica
   */
  getMissingDataByDate(
    startDate: string,
  ): Observable<StationMissingDataDailyModel[]> {
    return this.http
      .get<any>(`${this.baseUrl}missing-data?startDate=${startDate}`)
      .pipe(
        map((response: any) => {
          return response.data.map(StationMissingDataDailyAdapter.fromApi);
        }),
      );
  }

  /**
   * Cria um novo registro de dados meteorológicos com loading customizado
   */
  createWeatherDataWithCustomLoading(data: any): Observable<any> {
    const createUrl = this.baseUrl.replace(/\/$/, '');

    return this.http
      .post<any>(createUrl, data, { headers: this.skipLoadingHeaders })
      .pipe(map((response: any) => response.data));
  }

  /**
   * Cria múltiplos registros de dados meteorológicos em lote com loading customizado
   */
  createWeatherDataBatchWithCustomLoading(dataArray: any[]): Observable<any[]> {
    return this.http
      .post<any>(
        `${this.baseUrl}batch`,
        { data: dataArray },
        { headers: this.skipLoadingHeaders },
      )
      .pipe(map((response: any) => response.data));
  }
}
